/* eslint-disable quote-props, key-spacing, max-len, indent, max-statements */
import { NullableString } from "../../types/utility";

export enum Brand {
  BAMBOO = "bamboo",
  PLATA = "plata",
}

export enum Department {
  IVR = "ivr",
  LOYALTY = "loyalty",
  SERVICING = "servicing",
  COMPLAINTS = "complaints",
  ORIGINATIONS = "originations",
  UNDERWRITING = "underwriting",
  SERVICING_GEOGRAPHICAL = "servicing_geographical",
}

export const BRANDS = [
  Brand.BAMBOO,
  Brand.PLATA,
];

class PhoneNumber {
  number: string;

  constructor(number: string) {
    this.number = number;
  }

  to_intl() {
    return this.squished().replace("0", "+44");
  }

  squished() {
    return this.number.replace(/\s/g, "");
  }
}

type PhoneNumbersHash = {
  ivr: string
  loyalty: string
  servicing: string
  complaints: string
  originations: string
  underwriting: string
  servicing_geographical: string
}

class DepartmentPhoneNumbers {
  public ivr: PhoneNumber;
  public loyalty: PhoneNumber;
  public servicing: PhoneNumber;
  public complaints: PhoneNumber;
  public originations: PhoneNumber;
  public underwriting: PhoneNumber;
  public servicing_geographical: PhoneNumber;

  // eslint-disable-next-line max-statements
  constructor(phone_numbers: PhoneNumbersHash) {
    this.ivr = new PhoneNumber(phone_numbers.ivr);
    this.loyalty = new PhoneNumber(phone_numbers.loyalty);
    this.servicing = new PhoneNumber(phone_numbers.servicing);
    this.complaints = new PhoneNumber(phone_numbers.complaints);
    this.originations = new PhoneNumber(phone_numbers.originations);
    this.underwriting = new PhoneNumber(phone_numbers.underwriting);
    this.servicing_geographical = new PhoneNumber(phone_numbers.servicing_geographical);
  }
}

class EmailAddress {
  address: string;

  constructor(address: string) {
    this.address = address;
  }
}

type EmailAddressHash = {
  customer: string
  contact_us: string
  loyalty: string
  feedback: string
  customer_services: string
  complaints: string
  engineering_careers: string
  noreply: string
  compliance: string
}

class DepartmentEmailAddresses {
  public customer: EmailAddress
  public contact_us: EmailAddress
  public loyalty: EmailAddress
  public feedback: EmailAddress
  public customer_services: EmailAddress
  public complaints: EmailAddress
  public engineering_careers: EmailAddress
  public noreply: EmailAddress
  public compliance: EmailAddress

  constructor(email_addresses: EmailAddressHash) {
    this.customer = new EmailAddress(email_addresses.customer);
    this.contact_us = new EmailAddress(email_addresses.contact_us);
    this.loyalty = new EmailAddress(email_addresses.loyalty);
    this.feedback = new EmailAddress(email_addresses.feedback);
    this.customer_services = new EmailAddress(email_addresses.customer_services);
    this.complaints = new EmailAddress(email_addresses.complaints);
    this.engineering_careers = new EmailAddress(email_addresses.engineering_careers);
    this.noreply = new EmailAddress(email_addresses.noreply);
    this.compliance = new EmailAddress(email_addresses.compliance);
  }
}

type SmsNumbersHash = {
  general: string
  complaints: string
  talkdesk_api_triggered_flow: string
}

class DepartmentSmsNumbers {
  public general: PhoneNumber;
  public complaints: PhoneNumber;
  public talkdesk_api_triggered_flow: PhoneNumber;

  constructor(sms_numbers: SmsNumbersHash) {
    this.general = new PhoneNumber(sms_numbers.general);
    this.complaints = new PhoneNumber(sms_numbers.complaints);
    this.talkdesk_api_triggered_flow = new PhoneNumber(sms_numbers.talkdesk_api_triggered_flow);
  }
}

type Address = {
  sub_building_name: string
  building_name: string
  thoroughfare: NullableString
  post_town: string
  postcode: string
}

type CompanyLegal = {
  full_legal_name: string
  company_address: Address
  registration_number: string
  frn: string
  company_address_csv_delimited: string
}

type FaqPaths = {
  payout_information: string
  what_is_open_banking: string
  open_banking: string
  who_is_consents_online: string
  privacy_policy: string
  proof_document_info: string
  debit_card_not_accepted: string
  contact_us: string
  process_for_submitting_documents: string
  top_up_loan: string
  additional_help: string
  faq_root: string
  managing_your_loan: string
  your_loan_application: string
  early_settlement_information: string
  paylink_information: string
  debt_settlement: string
  what_is_a_debt_settlement_loan: string
  additional_borrowing: string
}
type FaqIds = {
  withdraw_loan_id: string
  debit_card_not_accepted_id: string
}

type TrustPilot = {
  brand_id: string
  review_url: string
  review_invite_url: string
}

type PaymentConfig = {
    maximum_amount: number,
    website_source: string
}

type BrandConfigHash = {
  brand: Brand,
  trading_name: string,
  company_legal: CompanyLegal,
  phone_numbers: DepartmentPhoneNumbers,
  sms_numbers: DepartmentSmsNumbers,
  email_addresses: DepartmentEmailAddresses,
  faq_paths: FaqPaths,
  send_declines_to_simply_money: boolean,
  display_direct_booking_rep_apr_note: boolean,
  trustpilot_config: TrustPilot,
  payment_config: PaymentConfig,
  faq_ids: FaqIds
}

export class BrandConfig {
  brand: Brand;
  trading_name: string;
  phone_numbers: DepartmentPhoneNumbers;
  sms_numbers: DepartmentSmsNumbers;
  email_addresses: DepartmentEmailAddresses;
  faq_paths: FaqPaths;
  company_legal: CompanyLegal;
  send_declines_to_simply_money: boolean;
  display_direct_booking_rep_apr_note: boolean;
  trustpilot_config: TrustPilot;
  payment_config: PaymentConfig;
  faq_ids: FaqIds;

  static for(brand: Brand) {
    switch (brand) {
      case Brand.BAMBOO:
        return BAMBOO_BRAND_CONFIG;
      case Brand.PLATA:
        return PLATA_BRAND_CONFIG;
      default:
        throw new Error(`No brand config for ${brand}`);
    }
  }

  constructor(config: BrandConfigHash) {
    this.brand = config.brand;
    this.trading_name = config.trading_name;
    this.phone_numbers = config.phone_numbers;
    this.sms_numbers = config.sms_numbers;
    this.email_addresses = config.email_addresses;
    this.faq_paths = config.faq_paths;
    this.company_legal = config.company_legal;
    this.send_declines_to_simply_money = config.send_declines_to_simply_money;
    this.display_direct_booking_rep_apr_note = config.display_direct_booking_rep_apr_note;
    this.trustpilot_config = config.trustpilot_config;
    this.payment_config = config.payment_config;
    this.faq_ids = config.faq_ids;
  }

  externalCallerId() {
    return this.phone_numbers.originations.to_intl();
  }
}

export const BAMBOO_BRAND_CONFIG = new BrandConfig({
  brand: Brand.BAMBOO,
  phone_numbers: new DepartmentPhoneNumbers({
    ivr: "0330 159 6010",
    loyalty: "0330 159 6014",
    servicing: "0330 159 6015",
    complaints: "02382 140 616",
    originations: "0330 159 6013",
    underwriting: "0330 159 6013",
    servicing_geographical: "02382 721 140",
  }),
  display_direct_booking_rep_apr_note: true,
  sms_numbers: new DepartmentSmsNumbers({
    general: "+447480559920",
    complaints: "+447897032226",
    talkdesk_api_triggered_flow: "+447700166910",
  }),
  trading_name: "Bamboo",
  company_legal: {
    full_legal_name: "Bamboo Limited",
    registration_number: "05629336",
    frn: "720565",
    company_address: {
  "sub_building_name": "1st Floor",
  "building_name": "Grenville House",
  "thoroughfare": "Nelson Gate",
  "post_town": "Southampton",
  "postcode": "SO15 1GX"
},
    company_address_csv_delimited: "1st Floor, Grenville House, Nelson Gate, Southampton, SO15 1GX",
  },
  email_addresses: new DepartmentEmailAddresses({
    customer: "customer@bamboo.test",
    contact_us: "contactus@bamboo.test",
    loyalty: "loyalty@bamboo.test",
    feedback: "feedback@bamboo.test",
    customer_services: "customerservices@bamboo.test",
    complaints: "complaints@bamboo.test",
    engineering_careers: "engineering.careers@bamboo.test",
    noreply: "noreply@bamboo.test",
    compliance: "compliance@bamboo.test",
  }),
  send_declines_to_simply_money: true,
  trustpilot_config: {
    brand_id: "5714c3620000ff00058baca4",
    review_url: "https://www.trustpilot.com/review/bambooloans.com",
    review_invite_url: "https://www.trustpilot.com/evaluate/bambooloans.com",
  },
  payment_config: {
    maximum_amount: 16500,
    website_source: "bamboo",
  },
  faq_paths: {
    payout_information: "/faq/what-time-do-bamboo-loans-payout",
    what_is_open_banking: "/faq/what-is-open-banking",
    open_banking: "/faq/what-is-open-banking",
    who_is_consents_online: "/faq/who-is-consents-online",
    privacy_policy: "/s/how-we-use-your-information",
    proof_document_info: "/faq/what-documents-will-i-need",
    debit_card_not_accepted: "/faq/debit-card-not-accepted",
    contact_us: "/s/contact-us",
    process_for_submitting_documents: "/faq/process-for-submitting-documents-online",
    top_up_loan: "/s/what-is-a-bamboo-top-up-loan",
    additional_help: "/s/additional-help",
    faq_root: "/faq",
    managing_your_loan: "/faq/managing-your-loan",
    your_loan_application: "/faq/your-loan-application",
    early_settlement_information: "/s/early-settlement-information",
    paylink_information: "/faq/paylink-information",
    debt_settlement: "/faq/debt-settlement",
    what_is_a_debt_settlement_loan: "/s/what-is-a-debt-settlement-loan",
    additional_borrowing: "/s/additional-borrowing",
  },
    faq_ids: {
    withdraw_loan_id: "ZSVZcBAAACQAeQ_d",
    debit_card_not_accepted_id: "ZSUbbxAAACMAd_EO",
  },
  });

export const PLATA_BRAND_CONFIG = new BrandConfig({
  brand: Brand.PLATA,
  phone_numbers: new DepartmentPhoneNumbers({
    ivr: "0330 165 5120",
    loyalty: "0330 165 5122",
    servicing: "0330 165 5125",
    complaints: "02382 544 129",
    originations: "0330 165 5121",
    underwriting: "0330 165 5121",
    servicing_geographical: "0117 463 0369",
  }),
  display_direct_booking_rep_apr_note: true,
  sms_numbers: new DepartmentSmsNumbers({
    general: "+447782621113",
    complaints: "+447782361117",
    talkdesk_api_triggered_flow: "+447893936650",
  }),
  trading_name: "Plata",
  company_legal: {
    full_legal_name: "Plata Finance Limited",
    registration_number: "05197592",
    frn: "718925",
    company_address: {
  "sub_building_name": "Frobisher House",
  "building_name": "Southbrook Rd",
  "thoroughfare": null,
  "post_town": "Southampton",
  "postcode": "SO15 1GX"
},
    company_address_csv_delimited: "Frobisher House, Southbrook Rd, Southampton, SO15 1GX",
  },
  email_addresses: new DepartmentEmailAddresses({
    customer: "contactus@plata.test",
    contact_us: "contactus@plata.test",
    loyalty: "loyalty@plata.test",
    feedback: "feedback@plata.test",
    customer_services: "customerservices@plata.test",
    complaints: "complaints@plata.test",
    engineering_careers: "engineering.careers@plata.test",
    noreply: "noreply@plata.test",
    compliance: "compliance@plata.test",
  }),
  send_declines_to_simply_money: false,
  trustpilot_config: {
    brand_id: "629dd1e10d89b370dab96fa8",
    review_url: "https://www.trustpilot.com/review/plata.com",
    review_invite_url: "https://www.trustpilot.com/evaluate/plata.com",
  },
  payment_config: {
    maximum_amount: 27500,
    website_source: "plata",
  },
  faq_paths: {
    payout_information: "/faq/payout-information",
    what_is_open_banking: "/s/what-is-open-banking",
    open_banking: "/faq/open-banking-information",
    who_is_consents_online: "/faq/who-is-consents-online",
    privacy_policy: "/s/privacy-policy",
    proof_document_info: "/s/supporting-documents",
    debit_card_not_accepted: "/faq/your-loan-application",
    contact_us: "/s/contact-us",
    process_for_submitting_documents: "/faq/supporting-documents",
    top_up_loan: "/s/top-up-loans",
    additional_help: "/s/accessibility-support",
    faq_root: "/faq",
    managing_your_loan: "/faq/managing-your-loan",
    your_loan_application: "/faq/your-loan-application",
    early_settlement_information: "/s/early-settlement-information",
    paylink_information: "/faq/paylink-information",
    debt_settlement: "/faq/debt-settlement",
    what_is_a_debt_settlement_loan: "/s/what-is-a-debt-settlement-loan",
    additional_borrowing: "/s/additional-borrowing",
  },
    faq_ids: {
    withdraw_loan_id: "YuEMfBAAACUAQjMo",
    debit_card_not_accepted_id: "Y-IhYxAAACYAHqD5",
  },
  });
